import { useState, useEffect } from "react";
import { apiClient } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Truck, 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Search,
  Filter,
  UserPlus,
  UserMinus,
  History,
  Wrench,
  ClipboardList,
  Building2,
  Calendar,
  DollarSign,
  Shield,
  Eye
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import VehicleDetailDialog from "@/components/VehicleDetailDialog";

const vehicleTypes = ["SUV", "Truck", "Pickup", "Heavy Truck", "Bus", "Sedan", "Van"];
const fuelTypes = ["diesel", "petrol", "electric", "hybrid"];
const statusOptions = [
  { value: "active", label: "Active", className: "bg-green-500/20 text-green-400 border-green-500/30" },
  { value: "maintenance", label: "Maintenance", className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { value: "out_of_service", label: "Out of Service", className: "bg-red-500/20 text-red-400 border-red-500/30" }
];

const formatCurrency = (value) => {
  if (!value) return "TZS 0";
  return new Intl.NumberFormat("en-TZ", {
    style: "currency",
    currency: "TZS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function VehiclesPage() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [vehicleHistory, setVehicleHistory] = useState({ services: [], repairs: [] });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    registration_number: "",
    make: "",
    model: "",
    year: new Date().getFullYear(),
    vehicle_type: "SUV",
    fuel_type: "diesel",
    organization_id: "",
    assigned_driver_id: "",
    status: "active",
    mileage: 0
  });

  const canEdit = user?.role === "admin" || user?.role === "fleet_manager";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, driversRes, orgsRes, workshopsRes] = await Promise.all([
        apiClient.get("/vehicles"),
        apiClient.get("/drivers"),
        apiClient.get("/organizations"),
        apiClient.get("/workshops")
      ]);
      setVehicles(vehiclesRes.data);
      setDrivers(driversRes.data);
      setOrganizations(orgsRes.data);
      setWorkshops(workshopsRes.data);
      
      if (orgsRes.data.length > 0 && !formData.organization_id) {
        setFormData(prev => ({ ...prev, organization_id: orgsRes.data[0].id }));
      }
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await apiClient.put(`/vehicles/${editingVehicle.id}`, formData);
        toast.success("Vehicle updated successfully");
      } else {
        await apiClient.post("/vehicles", formData);
        toast.success("Vehicle added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      registration_number: vehicle.registration_number,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      vehicle_type: vehicle.vehicle_type,
      fuel_type: vehicle.fuel_type,
      organization_id: vehicle.organization_id,
      assigned_driver_id: vehicle.assigned_driver_id || "none",
      status: vehicle.status,
      mileage: vehicle.mileage
    });
    setDialogOpen(true);
  };

  const handleDelete = async (vehicleId) => {
    if (!confirm("Are you sure you want to delete this vehicle?")) return;
    try {
      await apiClient.delete(`/vehicles/${vehicleId}`);
      toast.success("Vehicle deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete vehicle");
    }
  };

  const resetForm = () => {
    setEditingVehicle(null);
    setFormData({
      registration_number: "",
      make: "",
      model: "",
      year: new Date().getFullYear(),
      vehicle_type: "SUV",
      fuel_type: "diesel",
      organization_id: organizations[0]?.id || "",
      assigned_driver_id: "none",
      status: "active",
      mileage: 0
    });
  };

  const handleOpenAssignDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setSelectedDriverId(vehicle.assigned_driver_id || "unassign");
    setAssignDialogOpen(true);
  };

  const handleAssignDriver = async () => {
    if (!selectedVehicle) return;
    
    try {
      if (selectedDriverId && selectedDriverId !== "unassign") {
        // Create new assignment
        await apiClient.post("/driver-assignments", {
          vehicle_id: selectedVehicle.id,
          driver_id: selectedDriverId,
          start_date: new Date().toISOString().split("T")[0]
        });
        toast.success("Driver assigned successfully");
      } else {
        // Remove assignment
        const assignments = await apiClient.get(`/driver-assignments?vehicle_id=${selectedVehicle.id}&active_only=true`);
        if (assignments.data.length > 0) {
          await apiClient.delete(`/driver-assignments/${assignments.data[0].id}`);
        }
        toast.success("Driver unassigned successfully");
      }
      setAssignDialogOpen(false);
      setSelectedVehicle(null);
      setSelectedDriverId("unassign");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const getAvailableDrivers = () => {
    return drivers.filter(d => d.status === "available" || d.id === selectedVehicle?.assigned_driver_id);
  };

  const handleViewHistory = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setHistoryLoading(true);
    setHistoryDialogOpen(true);
    
    try {
      const [servicesRes, repairsRes] = await Promise.all([
        apiClient.get(`/service-records?vehicle_id=${vehicle.id}`),
        apiClient.get(`/repair-orders?vehicle_id=${vehicle.id}`)
      ]);
      
      setVehicleHistory({
        services: servicesRes.data,
        repairs: repairsRes.data
      });
    } catch (error) {
      toast.error("Failed to fetch vehicle history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const getWorkshopName = (workshopId) => {
    const workshop = workshops.find(w => w.id === workshopId);
    return workshop?.name || "Unknown Workshop";
  };

  const getTotalServiceCost = () => {
    return vehicleHistory.services.reduce((sum, s) => sum + (s.cost || 0), 0);
  };

  const getTotalRepairCost = () => {
    return vehicleHistory.repairs.reduce((sum, r) => sum + (r.actual_cost || r.estimated_cost || 0), 0);
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = 
      vehicle.registration_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || vehicle.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getDriverName = (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    return driver?.full_name || "Unassigned";
  };

  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find(s => s.value === status);
    return statusConfig || statusOptions[0];
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="vehicles-loading">
        <div className="skeleton-tactical h-8 w-48 rounded" />
        <div className="skeleton-tactical h-64 w-full rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight text-foreground">
            VEHICLE FLEET
          </h1>
          <p className="text-sm font-mono text-muted-foreground mt-1">
            Manage and monitor all fleet vehicles
          </p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="add-vehicle-btn">
                <Plus className="w-4 h-4 mr-2" />
                ADD VEHICLE
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border rounded-sm max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg tracking-wide">
                  {editingVehicle ? "EDIT VEHICLE" : "ADD NEW VEHICLE"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="vehicle-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Registration</Label>
                    <Input
                      value={formData.registration_number}
                      onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                      placeholder="T 123 ABC"
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="vehicle-registration-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Year</Label>
                    <Input
                      type="number"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="vehicle-year-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Make</Label>
                    <Input
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                      placeholder="Toyota"
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="vehicle-make-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Model</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="Land Cruiser"
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      required
                      data-testid="vehicle-model-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Vehicle Type</Label>
                    <Select value={formData.vehicle_type} onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}>
                      <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="vehicle-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-sm">
                        {vehicleTypes.map(type => (
                          <SelectItem key={type} value={type} className="font-mono text-sm">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Fuel Type</Label>
                    <Select value={formData.fuel_type} onValueChange={(value) => setFormData({ ...formData, fuel_type: value })}>
                      <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="vehicle-fuel-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-sm">
                        {fuelTypes.map(type => (
                          <SelectItem key={type} value={type} className="font-mono text-sm capitalize">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Status</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                      <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="vehicle-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-sm">
                        {statusOptions.map(option => (
                          <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">Mileage (km)</Label>
                    <Input
                      type="number"
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: parseInt(e.target.value) })}
                      className="bg-input border-border rounded-sm font-mono text-sm"
                      data-testid="vehicle-mileage-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-mono text-muted-foreground uppercase">Assigned Driver</Label>
                  <Select value={formData.assigned_driver_id || "none"} onValueChange={(value) => setFormData({ ...formData, assigned_driver_id: value === "none" ? "" : value })}>
                    <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="vehicle-driver-select">
                      <SelectValue placeholder="Select driver" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-sm">
                      <SelectItem value="none" className="font-mono text-sm">Unassigned</SelectItem>
                      {drivers.map(driver => (
                        <SelectItem key={driver.id} value={driver.id} className="font-mono text-sm">{driver.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button type="submit" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider" data-testid="vehicle-submit-btn">
                  {editingVehicle ? "UPDATE VEHICLE" : "ADD VEHICLE"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search vehicles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-input border-border rounded-sm font-mono text-sm"
                data-testid="vehicle-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-input border-border rounded-sm font-mono text-sm" data-testid="vehicle-filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm">
                <SelectItem value="all" className="font-mono text-sm">All Statuses</SelectItem>
                {statusOptions.map(option => (
                  <SelectItem key={option.value} value={option.value} className="font-mono text-sm">{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Vehicles Table */}
      <Card className="bg-card border-border rounded-sm" data-testid="vehicles-table">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">REGISTRATION</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">VEHICLE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">TYPE</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">STATUS</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">DRIVER</TableHead>
                  <TableHead className="font-heading text-xs tracking-wide text-muted-foreground">MILEAGE</TableHead>
                  {canEdit && <TableHead className="font-heading text-xs tracking-wide text-muted-foreground text-right">ACTIONS</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVehicles.map((vehicle) => {
                  const statusBadge = getStatusBadge(vehicle.status);
                  return (
                    <TableRow key={vehicle.id} className="border-border hover:bg-accent/50" data-testid={`vehicle-row-${vehicle.id}`}>
                      <TableCell className="font-mono text-sm font-medium text-primary">
                        {vehicle.registration_number}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{vehicle.make} {vehicle.model}</p>
                          <p className="text-xs font-mono text-muted-foreground">{vehicle.year}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {vehicle.vehicle_type}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusBadge.className} rounded-sm font-mono text-xs border`}>
                          {statusBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {getDriverName(vehicle.assigned_driver_id)}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {vehicle.mileage.toLocaleString()} km
                      </TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`vehicle-actions-${vehicle.id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
                              <DropdownMenuItem onClick={() => { setSelectedVehicle(vehicle); setDetailDialogOpen(true); }} className="cursor-pointer">
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleViewHistory(vehicle)} className="cursor-pointer">
                                <History className="w-4 h-4 mr-2" />
                                View History
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleOpenAssignDialog(vehicle)} className="cursor-pointer">
                                {vehicle.assigned_driver_id ? (
                                  <>
                                    <UserMinus className="w-4 h-4 mr-2" />
                                    Change Driver
                                  </>
                                ) : (
                                  <>
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Assign Driver
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEdit(vehicle)} className="cursor-pointer">
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Vehicle
                              </DropdownMenuItem>
                              {user?.role === "admin" && (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(vehicle.id)} 
                                  className="cursor-pointer text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {filteredVehicles.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8 text-muted-foreground font-mono">
                      No vehicles found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-primary">{vehicles.length}</p>
            <p className="text-xs font-mono text-muted-foreground">TOTAL VEHICLES</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-green-400">{vehicles.filter(v => v.status === "active").length}</p>
            <p className="text-xs font-mono text-muted-foreground">ACTIVE</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-yellow-400">{vehicles.filter(v => v.status === "maintenance").length}</p>
            <p className="text-xs font-mono text-muted-foreground">MAINTENANCE</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-heading font-bold text-red-400">{vehicles.filter(v => v.status === "out_of_service").length}</p>
            <p className="text-xs font-mono text-muted-foreground">OUT OF SERVICE</p>
          </CardContent>
        </Card>
      </div>

      {/* Assign Driver Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg tracking-wide">
              ASSIGN DRIVER
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedVehicle && (
              <div className="p-3 bg-accent/50 rounded-sm border border-border">
                <p className="text-xs font-mono text-muted-foreground">VEHICLE</p>
                <p className="text-sm font-medium text-primary">{selectedVehicle.registration_number}</p>
                <p className="text-xs text-muted-foreground">{selectedVehicle.make} {selectedVehicle.model}</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-xs font-mono text-muted-foreground uppercase">Select Driver</Label>
              <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="assign-driver-select">
                  <SelectValue placeholder="Select a driver" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border rounded-sm">
                  <SelectItem value="unassign" className="font-mono text-sm text-muted-foreground">
                    -- Unassign Driver --
                  </SelectItem>
                  {getAvailableDrivers().map(driver => (
                    <SelectItem key={driver.id} value={driver.id} className="font-mono text-sm">
                      {driver.full_name} ({driver.status})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs font-mono text-muted-foreground">
                Only available drivers are shown
              </p>
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setAssignDialogOpen(false)}
                className="flex-1 border-border text-foreground hover:bg-accent rounded-sm"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleAssignDriver}
                className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider"
                data-testid="confirm-assign-btn"
              >
                {selectedDriverId && selectedDriverId !== "unassign" ? "ASSIGN" : "UNASSIGN"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vehicle History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg tracking-wide flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              SERVICE & REPAIR HISTORY
            </DialogTitle>
          </DialogHeader>
          
          {selectedVehicle && (
            <div className="space-y-4">
              {/* Vehicle Info Header */}
              <Card className="bg-accent/30 border-primary/30 rounded-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-mono text-lg font-bold text-primary">{selectedVehicle.registration_number}</p>
                      <p className="text-sm text-foreground">{selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.year})</p>
                      <p className="text-xs font-mono text-muted-foreground mt-1">
                        Mileage: {selectedVehicle.mileage?.toLocaleString()} km | Type: {selectedVehicle.vehicle_type}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-mono text-muted-foreground">TOTAL COSTS</p>
                      <p className="text-xl font-heading font-bold text-yellow-400">
                        {formatCurrency(getTotalServiceCost() + getTotalRepairCost())}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Summary */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="bg-card border-border rounded-sm">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-lg font-heading font-bold text-foreground">{formatCurrency(getTotalServiceCost())}</p>
                      <p className="text-[10px] font-mono text-muted-foreground">SERVICE COSTS ({vehicleHistory.services.length} records)</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border rounded-sm">
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center">
                      <ClipboardList className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-lg font-heading font-bold text-foreground">{formatCurrency(getTotalRepairCost())}</p>
                      <p className="text-[10px] font-mono text-muted-foreground">REPAIR COSTS ({vehicleHistory.repairs.length} orders)</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* History Tabs */}
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-primary animate-pulse font-mono">Loading history...</div>
                </div>
              ) : (
                <Tabs defaultValue="services" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-muted rounded-sm">
                    <TabsTrigger value="services" className="rounded-sm font-heading tracking-wide text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      <Wrench className="w-3 h-3 mr-2" />
                      SERVICE RECORDS ({vehicleHistory.services.length})
                    </TabsTrigger>
                    <TabsTrigger value="repairs" className="rounded-sm font-heading tracking-wide text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                      <ClipboardList className="w-3 h-3 mr-2" />
                      REPAIR ORDERS ({vehicleHistory.repairs.length})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="services" className="mt-4">
                    <ScrollArea className="h-[280px]">
                      {vehicleHistory.services.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground font-mono text-sm">
                          No service records found for this vehicle
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {vehicleHistory.services
                            .sort((a, b) => new Date(b.service_date) - new Date(a.service_date))
                            .map((service) => (
                            <Card key={service.id} className="bg-accent/20 border-border rounded-sm">
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 rounded-sm font-mono text-[10px]">
                                        {service.service_type}
                                      </Badge>
                                      <span className="text-xs font-mono text-muted-foreground flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {service.service_date}
                                      </span>
                                    </div>
                                    <p className="text-sm text-foreground mt-2">{service.description}</p>
                                    <div className="flex items-center gap-4 mt-2 text-xs font-mono text-muted-foreground">
                                      <span>Mileage: {service.mileage_at_service?.toLocaleString()} km</span>
                                      {service.next_service_date && (
                                        <span className="text-yellow-400">Next: {service.next_service_date}</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-lg font-heading font-bold text-yellow-400">
                                      {formatCurrency(service.cost)}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="repairs" className="mt-4">
                    <ScrollArea className="h-[280px]">
                      {vehicleHistory.repairs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground font-mono text-sm">
                          No repair orders found for this vehicle
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {vehicleHistory.repairs
                            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                            .map((repair) => (
                            <Card key={repair.id} className="bg-accent/20 border-border rounded-sm">
                              <CardContent className="p-3">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-mono text-xs text-primary font-medium">{repair.order_number}</span>
                                      <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 rounded-sm font-mono text-[10px]">
                                        {repair.repair_type}
                                      </Badge>
                                      <Badge className={`rounded-sm font-mono text-[10px] ${
                                        repair.status === 'completed' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                                        repair.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                                        'bg-gray-500/20 text-gray-400 border-gray-500/30'
                                      }`}>
                                        {repair.status}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-foreground mt-2">{repair.description}</p>
                                    <p className="text-xs text-muted-foreground mt-1">Issue: {repair.reported_issue}</p>
                                    <div className="flex items-center gap-2 mt-2 text-xs font-mono text-muted-foreground">
                                      <Building2 className="w-3 h-3" />
                                      <span>{getWorkshopName(repair.workshop_id)}</span>
                                      {repair.scheduled_date && (
                                        <>
                                          <span className="mx-1">|</span>
                                          <Calendar className="w-3 h-3" />
                                          <span>{repair.scheduled_date}</span>
                                        </>
                                      )}
                                    </div>
                                    {repair.assigned_mechanic && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Mechanic: {repair.assigned_mechanic}
                                      </p>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <p className="text-lg font-heading font-bold text-yellow-400">
                                      {formatCurrency(repair.actual_cost || repair.estimated_cost)}
                                    </p>
                                    <p className="text-[10px] font-mono text-muted-foreground">
                                      {repair.actual_cost ? 'ACTUAL' : 'ESTIMATED'}
                                    </p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vehicle Detail Dialog (Warranty & Detailed History) */}
      <VehicleDetailDialog 
        vehicle={selectedVehicle}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
      />
    </div>
  );
}
